import {injectIntl} from "react-intl";
import React, {useContext, useEffect, useRef, useState} from "react";
import {AutoRetrieverDeviceOverviewContext} from "./context/AutoRetrieverDeviceOverviewContext";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import {
    ASCENDING, ASSET_CONFIG_ATTRIBUTES, AUTO_RETRIEVER_ASSET_TYPE, CONFIG_TYPE, PICK_TEST_TAB, SEARCH_ROW_COLOR
} from "../../util/Constants";
import Tab, {TabGroup} from "@amzn/meridian/tab";
import PickTestExecution from "./PickTestExecution";
import {
    chainWalk,
    getAssetRefreshInterval,
    getFormattedTime,
    isTimestampWithinHours,
    sortComparator
} from "../../util/Util";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {TableCell, TableRow} from "@amzn/meridian/table";
import {getLocalizedMessage} from "../../util/Localization";
import Input from "@amzn/meridian/input";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small"
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small"
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import refreshTokens from "@amzn/meridian-tokens/base/icon/refresh"
import Tooltip from "@amzn/meridian/tooltip";
import PickStationConfiguration from "./PickStationConfiguration";
import RetrieverLocationMappingConfiguration from "./RetrieverLocationMappingConfiguration";
import RollbackModal from "../../rollback/RollbackModal";
import {getAssetVersions, getAssetByVersion} from "../../resources/AssetResource";

const AR_CONFIG_ATTRIBUTES = ASSET_CONFIG_ATTRIBUTES[AUTO_RETRIEVER_ASSET_TYPE];

const AutoRetrieverDeviceConfiguration = ({intl}) => {

    //Add Context
    const {viewModel, dispatch} = useContext(AutoRetrieverDeviceOverviewContext);
    const {getDraftData, setDraftData, getFilteredDraftData, resetDraftData, isDataEdited} = useContext(DraftContext);
    const { showCustomAlert } = useContext(GlobalStateContext);

    const [selectedMac, setSelectedMac] = useState("");
    const [valueConfiguration, setValueConfiguration] = useState("");
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [testConfigSortColumn, setTestConfigSortColumn] = useState("id");
    const [testConfigSortOrder, setTestConfigSortOrder] = useState(ASCENDING);
    const [expandedRows, setExpandedRows] = useState([]);
    const [showRollbackModal, setShowRollbackModal] = useState(false);
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [isPickStationEditMode, setIsPickStationEditMode] = useState(false);
    const [pickTestTab, setPickTestTab] = useState(PICK_TEST_TAB.PICKLISTS);

    const tableEndRef = useRef(null);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION, CONFIG_TYPE.PICK_STATION_CONFIGURATION, CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING];

    useEffect(() => {
        getData()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getData();
            }
        }, getAssetRefreshInterval());
        return () => clearInterval(interval);
    }, []);

    // Pick Test is read-only, so refresh its draft whenever the device's test config changes
    // (e.g. after Start Test generates new picklists) instead of only on device selection.
    const selectedTestConfigTimestamp = chainWalk(() => viewModel.testConfig[selectedMac].timestamp, null);
    useEffect(() => {
        setDraftData({
            data: chainWalk(() => extractTestConfigForPick(viewModel.testConfig[selectedMac]), []),
            stateKey: CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }, [selectedMac, selectedTestConfigTimestamp]);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.autoRetrieverDeviceInformation[selectedMac].retrieverLocationMapping, []),
            stateKey: CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const getData = () => {
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const onClickStartTest = () => {
        dispatch({type: "START_MOCK_TEST", data: {
            dispatcher: dispatch,
            showCustomAlert: showCustomAlert,
            mac: selectedMac
        }})
    }

    const isValidTestConfig = () => {
        const config = viewModel.testConfig[selectedMac];
        return config && config.picklists && isTimestampWithinHours(config.timestamp, 8);
    }

    const extractTestConfigForPick = (testConfigForAR) => {
        const result = [];
        if (!isValidTestConfig()) {
            return result;
        }
        testConfigForAR.picklists.forEach((picklist) => {
            const allPickItemsForPicklist = [];
            Object.keys(picklist.pickItems).forEach((cluster) => {
                picklist.pickItems[cluster].forEach((item) => {
                    item.cluster = cluster;
                    allPickItemsForPicklist.push(item);
                });
            });
            result.push({
                id: picklist.id + "#" + picklist.groupId,
                retrievalLocation: picklist.retrievalLocation,
                pickItems: allPickItemsForPicklist
            })
        });
        return result;
    }


    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const updateExpandedRows = row => {
        const newExpandedRows = expandedRows.includes(row)
            ? expandedRows.filter(expandedRow => expandedRow !== row)
            : [...expandedRows, row]

        setExpandedRows(newExpandedRows)
    }

    const getSortedDeviceList = () => {
        // Device List sorted based on device "name"
        const sortedDeviceList = Object.values(viewModel.autoRetrieverDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION,
            subKey: selectedMac
        });

        data = data.filter(item => getTestConfigKey(item.pickItems)
            .toLowerCase()
            .includes(scannablesSearchInput.toLowerCase()))

        data = data.sort((a, b)=>
            sortTestConfigColumns[testConfigSortColumn](a, b, testConfigSortOrder === ASCENDING)
        )

        return data;
    }

    const getTestConfigKey = (pickItems) => {
        return pickItems.map(item => item.id).join(';');
    }

    const sortTestConfigColumns = {
        id: (a, b, ascending) => {
            return ascending ? sortComparator(a.id, b.id) : -sortComparator(a.id, b.id);
        },
        pickItemsCount: (a, b, ascending) => {
            return ascending ? sortComparator(a.pickItems.length, b.pickItems.length) : -sortComparator(a.pickItems.length, b.pickItems.length);
        },
        retrievalLocation: (a, b, ascending) => {
            return ascending ? sortComparator(a.retrievalLocation, b.retrievalLocation) : -sortComparator(a.retrievalLocation, b.retrievalLocation);
        }
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell columnSpan={3}/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="100%"
                        size="small"/>
                </TableCell>
                <TableCell columnSpan={3}/>
            </TableRow>
        )
    }

    const renderTestConfigExpandableTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell width={60}>
                    {/* Empty table cell to hold the expandable Icon button */}
                </TableCell>
                <TableCell sortColumn="id">
                    {getLocalizedMessage({intl, stringId: "table.index"})}
                </TableCell>
                <TableCell sortColumn="pickItemsCount">
                    {getLocalizedMessage({intl, stringId: "testconfig.items_count"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_item"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_item_type"})}
                </TableCell>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "testconfig.cluster"})}
                </TableCell>
                <TableCell sortColumn="retrievalLocation">
                    {getLocalizedMessage({intl, stringId: "testconfig.pick_point"})}
                </TableCell>
            </TableRow>
        )
    }

    const renderTestConfigExpandableTableRow = (config, serialNumber) => {
        let rows = [];
        rows.push(<TableRow>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Button
                    type="icon"
                    size="small"
                    onClick={() => updateExpandedRows(config.id)}>
                    {expandedRows.includes(config.id) ? (
                        <Icon tokens={chevronDownSmallTokens}>Collapse rows</Icon>
                    ) : (
                        <Icon tokens={chevronRightSmallTokens}>Expand rows</Icon>
                    )}
                </Button>
            </TableCell>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Column spacingInset="200 none none">{config.id}</Column>
            </TableCell>
            <TableCell
                rowSpan={expandedRows.includes(config.id) ? config.pickItems.length : 1}
                alignmentVertical="top">
                <Column spacingInset="200 none none">{config.pickItems.length} items</Column>
            </TableCell>
            <TableCell>{config.pickItems[0].id}</TableCell>
            <TableCell>{config.pickItems[0].type}</TableCell>
            <TableCell>{config.pickItems[0].cluster}</TableCell>
            <TableCell>{config.retrievalLocation}</TableCell>
        </TableRow>);
        if (expandedRows.includes(config.id)) {
            config.pickItems.slice(1).map(item => (
                rows.push(<TableRow>
                    <TableCell showCell={false}>{/* Empty table cell */}</TableCell>
                    <TableCell showCell={false}>{/* Empty table cell */}</TableCell>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>{item.type}</TableCell>
                    <TableCell>{item.cluster}</TableCell>
                    <TableCell>{config.retrievalLocation}</TableCell>
                </TableRow>)
            ))
        }
        return rows;
    }

    const deployPickStationConfig = () => {
        const configData = getFilteredDraftData({
            stateKey: CONFIG_TYPE.PICK_STATION_CONFIGURATION,
            subKey: selectedMac
        });
        dispatch({
            type: "SAVE_PICK_STATION_CONFIG",
            data: {
                dispatcher: dispatch,
                mac: selectedMac,
                config: configData,
                showCustomAlert: showCustomAlert
            }
        });
        setDraftData({
            data: configData,
            stateKey: CONFIG_TYPE.PICK_STATION_CONFIGURATION,
            subKey: selectedMac,
            override: true
        });
        setIsPickStationEditMode(false);
    };

    const deployLocationMapping = () => {
        const configData = getFilteredDraftData({
            stateKey: CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING,
            subKey: selectedMac
        });
        dispatch({
            type: "SAVE_RETRIEVER_LOCATION_MAPPING",
            data: {
                dispatcher: dispatch,
                mac: selectedMac,
                config: configData,
                showCustomAlert: showCustomAlert
            }
        });
        setDraftData({
            data: configData,
            stateKey: CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING,
            subKey: selectedMac,
            override: true
        });
    };

    const getRollbackVersions = async () => {
        setIsVersionsLoading(true);
        const versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(chainWalk(() => versionsData.data.assetVersionDetailsList, []));
        setIsVersionsLoading(false);
    };

    const loadRollbackDataToDraft = async () => {
        const rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        if (isPickStationConfig) {
            const pickStationConfigData = chainWalk(
                () => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[AR_CONFIG_ATTRIBUTES.PICK_STATION_CONFIG]), []);
            if (pickStationConfigData.length) {
                setDraftData({
                    data: pickStationConfigData,
                    stateKey: CONFIG_TYPE.PICK_STATION_CONFIGURATION,
                    subKey: selectedMac,
                    allRowsAdded: true,
                    override: true
                });
            }
        }
        if (isLocationMapping) {
            const locationMappingData = chainWalk(
                () => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes[AR_CONFIG_ATTRIBUTES.LOCATION_MAPPING]), []);
            if (locationMappingData.length) {
                setDraftData({
                    data: locationMappingData,
                    stateKey: CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING,
                    subKey: selectedMac,
                    allRowsAdded: true,
                    override: true
                });
            }
        }
    };

    const isPickStationConfig = valueConfiguration === CONFIG_TYPE.PICK_STATION_CONFIGURATION;
    const isLocationMapping = valueConfiguration === CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING;

    const getHeaderProps = () => {
        const baseProps = {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            defaultSelection: "",
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp]
        };
        if (isPickStationConfig) {
            return {
                ...baseProps,
                deployDraft: deployPickStationConfig,
                resetDraftData: (stateKeys, subKey) => {
                    resetDraftData(stateKeys, subKey);
                    setIsPickStationEditMode(false);
                },
                isDeployDisabled: !isDataEdited([CONFIG_TYPE.PICK_STATION_CONFIGURATION]),
                isRollbackDisabled: !selectedMac || isDataEdited([CONFIG_TYPE.PICK_STATION_CONFIGURATION]),
                rollbackClickHandler: () => { getRollbackVersions(); setShowRollbackModal(true); }
            };
        }
        if (isLocationMapping) {
            return {
                ...baseProps,
                deployDraft: deployLocationMapping,
                resetDraftData: () => resetDraftData([CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING], selectedMac),
                isDeployDisabled: !isDataEdited([CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING]),
                isRollbackDisabled: !selectedMac || isDataEdited([CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING]),
                rollbackClickHandler: () => { getRollbackVersions(); setShowRollbackModal(true); }
            };
        }
        return {...baseProps, isViewOnly: true};
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                {valueConfiguration === CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION &&
                    selectedMac &&
                    <TabGroup value={pickTestTab} onChange={setPickTestTab} fill="line">
                        <Tab value={PICK_TEST_TAB.PICKLISTS}>
                            {getLocalizedMessage({intl, stringId: "pickexecution.tab.picklists"})}
                        </Tab>
                        <Tab value={PICK_TEST_TAB.EXECUTION}>
                            {getLocalizedMessage({intl, stringId: "pickexecution.tab.execution"})}
                        </Tab>
                    </TabGroup>
                }
                {valueConfiguration === CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION &&
                    selectedMac && pickTestTab === PICK_TEST_TAB.EXECUTION &&
                    <PickTestExecution key={selectedMac} selectedMac={selectedMac}
                                       testConfig={isValidTestConfig() ? viewModel.testConfig[selectedMac] : null}/>
                }
                {valueConfiguration === CONFIG_TYPE.AUTO_RETRIEVER_TEST_CONFIGURATION &&
                    selectedMac && pickTestTab === PICK_TEST_TAB.PICKLISTS &&
                    <Column>
                        { isValidTestConfig() &&
                            <PaginatedTable
                            data={searchDataForTestConfigTable()}
                            renderHeader={renderTestConfigExpandableTableHeader}
                            renderSearchRow={renderSearchRow}
                            renderRow={renderTestConfigExpandableTableRow}
                            sortColumn={testConfigSortColumn}
                            sortDirection={testConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setTestConfigSortColumn(sortColumn);
                                setTestConfigSortOrder(sortDirection);
                            }}/>
                        }
                        <Row width="100%" widths={["100", "fill", "100"]}>
                            <Tooltip position="end"
                                     title={getLocalizedMessage({intl, stringId: "tooltip.start_retrieval_test_desc"})}>
                                <Button onClick={onClickStartTest}
                                        size="medium" type="primary">
                                    <Icon tokens={refreshTokens}/>
                                    {getLocalizedMessage({intl, stringId: "testconfig.start"})}
                                </Button>
                            </Tooltip>
                            <Box></Box>
                            { isValidTestConfig() &&
                                <Text type="b200">
                                    {getLocalizedMessage({intl, stringId: "common.data_created"})}
                                    : {getFormattedTime(intl.locale, viewModel.testConfig[selectedMac].timestamp)}
                                </Text>
                            }
                        </Row>
                        <div ref={tableEndRef}/>
                    </Column>
                }
                {valueConfiguration === CONFIG_TYPE.PICK_STATION_CONFIGURATION &&
                    selectedMac &&
                    <PickStationConfiguration selectedMac={selectedMac}
                                              isEditMode={isPickStationEditMode}
                                              setIsEditMode={setIsPickStationEditMode}/>
                }
                {valueConfiguration === CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING &&
                    selectedMac &&
                    <RetrieverLocationMappingConfiguration selectedMac={selectedMac}/>
                }
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
            </Column>
        )
    }

}

export default injectIntl(AutoRetrieverDeviceConfiguration);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/AutoRetrieverDeviceConfiguration.js